import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Search, Filter, Home, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const STAGE_LABELS = {
  application_submitted: 'Application Submitted',
  under_vetting: 'Under Vetting',
  awaiting_dropoff: 'Awaiting Drop-off',
  agreement_sent: 'Agreement Signed',
  awaiting_checkin: 'Awaiting Check-in',
  in_preparation: 'In Preparation',
  live_on_marketplace: 'Live on Marketplace',
  sold: 'Sold'
};

const STAGE_COLORS = {
  application_submitted: 'bg-red-100 text-red-700',
  under_vetting: 'bg-yellow-100 text-yellow-700',
  awaiting_dropoff: 'bg-blue-100 text-blue-700',
  agreement_sent: 'bg-indigo-100 text-indigo-700',
  awaiting_checkin: 'bg-purple-100 text-purple-700',
  in_preparation: 'bg-purple-100 text-purple-700',
  live_on_marketplace: 'bg-green-100 text-green-700',
  sold: 'bg-teal-100 text-teal-700'
};

export default function ApplicationsList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stageFilter = searchParams.get('stage') || 'all';

  const [applications, setApplications] = useState([]);
  const [items, setItems] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState(stageFilter);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const rowKey = (row) => `${row.type}:${row.id}`;

  const toggleSelect = (row) => {
    const key = rowKey(row);
    setSelectedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleSelectAll = () => {
    setSelectedKeys(prev =>
      prev.length === combinedData.length ? [] : combinedData.map(rowKey)
    );
  };

  const bulkDelete = async () => {
    setBulkDeleting(true);
    let ok = 0, failed = 0;
    for (const key of selectedKeys) {
      const [type, id] = key.split(':');
      try {
        await axios.delete(`${API}/${type === 'item' ? 'items' : 'applications'}/${id}`, { withCredentials: true });
        ok += 1;
      } catch (e) {
        failed += 1;
      }
    }
    setBulkDeleting(false);
    setBulkConfirmOpen(false);
    setSelectedKeys([]);
    if (ok) toast.success(`Deleted ${ok} record${ok === 1 ? '' : 's'}`);
    if (failed) toast.error(`${failed} record${failed === 1 ? '' : 's'} could not be deleted`);
    loadData();
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [appsRes, itemsRes] = await Promise.all([
        axios.get(`${API}/applications`, { withCredentials: true }),
        axios.get(`${API}/items`, { withCredentials: true })
      ]);

      setApplications(appsRes.data || []);
      setItems(itemsRes.data || []);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  const filterData = useCallback(() => {
    let combined = [];

    // Add applications (stages 1-3)
    applications.forEach(app => {
      combined.push({
        id: app.id,
        reference_code: app.reference_code,
        type: 'application',
        seller_name: app.seller?.full_name || 'Unknown',
        item_title: app.item_title,
        ai_vetting: app.ai_vetting,
        category: app.category,
        price: app.nominated_sale_price,
        stage: app.lifecycle_stage,
        submitted_at: app.submitted_at,
        seller_id: app.seller_id
      });
    });

    // Add items (stages 4-8)
    items.forEach(item => {
      combined.push({
        id: item.id,
        reference_code: 'N/A',
        type: 'item',
        seller_name: item.seller?.full_name || 'Unknown',
        item_title: item.title,
        category: item.category,
        price: item.listed_price || item.nominated_sale_price || item.sale_price,
        stage: item.lifecycle_stage,
        submitted_at: item.created_at,
        seller_id: item.seller_id
      });
    });

    // Filter by stage
    if (filterStage && filterStage !== 'all') {
      combined = combined.filter(item => item.stage === filterStage);
    }

    // Filter by search
    if (searchQuery) {
      combined = combined.filter(item => 
        item.reference_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.seller_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.item_title?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort by date
    combined.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));

    setCombinedData(combined);
  }, [filterStage, searchQuery, applications, items]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    filterData();
  }, [filterData]);

  const handleRowClick = (item) => {
    if (item.type === 'application') {
      navigate(`/application/${item.id}`);
    } else {
      navigate(`/item/${item.id}`);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStageCount = (stage) => {
    if (stage === 'all') return applications.length + items.length;
    return combinedData.filter(item => item.stage === stage).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <p className="text-[#474747]">Loading applications...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E9E9E9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between mb-4 gap-2">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#474747]"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Dashboard</span>
              </button>
            </div>
            <Button
              onClick={() => navigate('/apply')}
              className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Application
            </Button>
          </div>

          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-[#474747] mb-4">Applications</h1>

          {/* Search and Filters */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search applications..."
                className="w-full pl-10 pr-4 py-2 border border-[#E9E9E9] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#C6E76C]/50"
              />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        {/* Stage Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setFilterStage('all')}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              filterStage === 'all'
                ? 'bg-[#C6E76C] text-[#474747]'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
            }`}
          >
            All ({getStageCount('all')})
          </button>
          {Object.entries(STAGE_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilterStage(key)}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                filterStage === key
                  ? 'bg-[#C6E76C] text-[#474747]'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
              }`}
            >
              {label} ({getStageCount(key)})
            </button>
          ))}
        </div>

        {/* Bulk actions bar */}
        {selectedKeys.length > 0 && (
          <div className="flex items-center justify-between bg-[#1E2117] text-white rounded-lg px-4 py-3 mb-4" data-testid="bulk-actions-bar">
            <span className="text-sm font-medium">{selectedKeys.length} selected</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="bg-transparent border-gray-500 text-gray-200 hover:bg-gray-800 hover:text-white"
                onClick={() => setSelectedKeys([])}
                data-testid="bulk-clear-button"
              >
                Clear
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => setBulkConfirmOpen(true)}
                data-testid="bulk-delete-button"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Selected ({selectedKeys.length})
              </Button>
            </div>
          </div>
        )}

        {/* Mobile card list */}
        <div className="md:hidden space-y-3" data-testid="applications-mobile-list">
          {combinedData.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#E9E9E9] p-8 text-center text-gray-500 text-sm">No applications found</div>
          ) : (
            combinedData.map(item => (
              <div
                key={rowKey(item)}
                onClick={() => handleRowClick(item)}
                className="bg-white rounded-xl border border-[#E9E9E9] p-4 active:bg-gray-50"
                data-testid={`app-card-${item.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(rowKey(item))}
                      onChange={() => toggleSelect(item)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 mt-1 accent-[#C6E76C] shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#474747] leading-snug">{item.item_title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{item.seller_name}{item.reference_code && item.reference_code !== 'N/A' ? ` • ${item.reference_code}` : ''}</p>
                    </div>
                  </div>
                  <p className="text-sm font-bold text-[#474747] shrink-0">{item.price ? `$${item.price.toLocaleString()}` : ''}</p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-2.5 pl-7">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${STAGE_COLORS[item.stage] || 'bg-gray-100 text-gray-700'}`}>
                    {STAGE_LABELS[item.stage] || item.stage}
                  </span>
                  {item.ai_vetting && (
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                      item.ai_vetting.recommendation === 'approve' ? 'bg-green-100 text-green-700'
                        : item.ai_vetting.recommendation === 'decline' ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      AI {item.ai_vetting.score}
                    </span>
                  )}
                  <span className="text-[11px] text-gray-400 ml-auto">{formatDate(item.submitted_at)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Table (desktop) */}
        <div className="hidden md:block bg-white rounded-xl border border-[#E9E9E9] overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-[#E9E9E9]">
              <tr>
                <th className="pl-6 pr-2 py-4 w-10">
                  <input
                    type="checkbox"
                    checked={combinedData.length > 0 && selectedKeys.length === combinedData.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 accent-[#C6E76C] cursor-pointer"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Reference
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Seller
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Stage
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E9E9E9]">
              {combinedData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    No applications found
                  </td>
                </tr>
              ) : (
                combinedData.map(item => (
                  <tr
                    key={item.id}
                    onClick={() => handleRowClick(item)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="pl-6 pr-2 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedKeys.includes(rowKey(item))}
                        onChange={() => toggleSelect(item)}
                        className="w-4 h-4 accent-[#C6E76C] cursor-pointer"
                        data-testid={`select-row-${item.id}`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#474747]">
                        {item.reference_code || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-[#474747]">{item.seller_name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#474747]">{item.item_title}</p>
                      {item.ai_vetting && (
                        <span className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                          item.ai_vetting.recommendation === 'approve' ? 'bg-green-100 text-green-700'
                            : item.ai_vetting.recommendation === 'decline' ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`} data-testid={`ai-badge-${item.id}`}>
                          AI {item.ai_vetting.score} · {item.ai_vetting.recommendation}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{item.category || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${STAGE_COLORS[item.stage] || 'bg-gray-100 text-gray-700'}`}>
                        {STAGE_LABELS[item.stage] || item.stage}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#474747]">
                        {item.price ? `$${item.price.toLocaleString()}` : 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(item.submitted_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk delete confirmation */}
      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete {selectedKeys.length} selected record{selectedKeys.length === 1 ? '' : 's'}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This permanently deletes the selected records and their related bookings, tasks and activity. This cannot be undone.
          </p>
          <p className="text-xs text-gray-500">
            Note: deleting an application does <strong>not</strong> delete any item that was created from it — items must be deleted individually from the item page.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setBulkConfirmOpen(false)} data-testid="bulk-cancel-button">Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-confirm-delete-button"
            >
              {bulkDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
